/**
 * Cheap feedback for events that carry a position: a soft pastel puff that
 * blooms and fades where something was chopped, dug or placed. One shared
 * texture, a fixed pool of sprites, no allocation while running.
 */
import * as THREE from 'three';

const POOL = 20;
const LIFE = 0.75; // seconds

function puffTexture(): THREE.Texture {
  const size = 96;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 2, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,0.95)');
  g.addColorStop(0.45, 'rgba(255,255,255,0.35)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

interface Puff {
  sprite: THREE.Sprite;
  age: number;
  alive: boolean;
}

export class Effects {
  readonly group = new THREE.Group();
  private puffs: Puff[] = [];
  private next = 0;

  constructor() {
    this.group.name = 'effects';
    const map = puffTexture();
    for (let i = 0; i < POOL; i++) {
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({ map, transparent: true, depthWrite: false, opacity: 0 }),
      );
      sprite.visible = false;
      sprite.renderOrder = 5;
      this.group.add(sprite);
      this.puffs.push({ sprite, age: 0, alive: false });
    }
  }

  pop(x: number, y: number, z: number, color: string): void {
    const puff = this.puffs[this.next];
    this.next = (this.next + 1) % this.puffs.length;
    puff.age = 0;
    puff.alive = true;
    puff.sprite.visible = true;
    puff.sprite.position.set(x, y + 0.35, z);
    (puff.sprite.material as THREE.SpriteMaterial).color.set(color);
  }

  update(dt: number): void {
    for (const puff of this.puffs) {
      if (!puff.alive) continue;
      puff.age += dt;
      const t = puff.age / LIFE;
      if (t >= 1) {
        puff.alive = false;
        puff.sprite.visible = false;
        continue;
      }
      const s = 0.3 + t * 0.7;
      puff.sprite.scale.set(s, s, 1);
      puff.sprite.position.y += dt * 0.35;
      (puff.sprite.material as THREE.SpriteMaterial).opacity = (1 - t) * 0.75;
    }
  }
}
